import type { ReplyDraftErrorKind } from '../model/errors';
import type { WizardStepKey } from '../model/step';
import type { REPLY_DRAFT_TOUR_ZONES, ReplyDraftTourSegment } from '../model/tour';

type TourZoneNumber<Segment extends ReplyDraftTourSegment> = Extract<
  (typeof REPLY_DRAFT_TOUR_ZONES)[Segment][keyof (typeof REPLY_DRAFT_TOUR_ZONES)[Segment]],
  number
>;

/** One line of copy for every zone the tour can highlight, checked against the zone table. */
type TourStepCopy = {
  [Segment in ReplyDraftTourSegment]: Record<TourZoneNumber<Segment>, string>;
};

export const REPLY_DRAFT_COPY = {
  title: 'Draft your reply',
  close: 'Close',
  closeLabel: 'Close the reply draft',
  loading: 'Loading your draft…',
  loadFailed: 'Could not load this draft.',
  noDraft: 'No reply draft is ready for this inquiry yet.',
  retry: 'Try again',

  steps: { card: 'Guidance', review: 'Points' } satisfies Record<WizardStepKey, string>,
  composeStep: 'Review',
  stepTitles: {
    card: 'Pick your guidance',
    review: 'Add or remove points',
  } satisfies Record<WizardStepKey, string>,
  stepAnnouncement: (number: number, total: number, title: string) =>
    `Step ${number} of ${total}, ${title}`,
  back: 'Back',
  backHint: 'Goes to the previous step. Your changes are kept.',
  next: 'Continue',
  nextHint: 'Goes to the next step',
  keepPosition: 'Keep this guidance',
  keepPositionHint: 'Goes back to your draft without rewriting it',
  currentPosition: 'Current',
  currentPositionLabel: (label: string) => `${label}, your current guidance`,

  historyHeading: (count: number | null) =>
    count === null ? 'Your similar cases' : `Your similar cases (${count})`,
  historyEmpty: 'No similar cases of yours on record.',
  changedRecentlyHeading: (count: number) => `Changed recently (${count})`,
  changedRecentlyEmpty: 'No new approvals, guideline updates or trial readouts found.',
  caseUpdated: (when: string) =>
    `The asker wrote again ${when}. This draft does not include that message.`,
  chooseInstruction:
    'Pick the guidance closest to yours. It is only where your draft starts: next you add or remove any point, then review the reply before you send it. Nothing sends without you.',
  researchHeading: 'Research',
  selectOption: 'Select',
  optionHint: 'Starts your draft from this guidance',
  somethingElse: 'Something else',
  somethingElsePlaceholder: 'Type your guidance',
  somethingElseSubmit: 'Draft from this',
  writing: 'Writing your draft…',
  otherFailed: 'Could not write a draft from that. Try again.',
  peersHeading: (count: number) => `How ${count} peers answered`,
  somethingElseRow: 'Something else…',

  yourGuidance: 'Your guidance',
  pointsHeading: (included: number, total: number) =>
    `Points: ${included} of ${total} in your reply`,
  pointsIntro: "Tap − to take out anything you wouldn't say, or + to add a point to your reply.",
  inReplyHeading: 'In your reply',
  notInReplyHeading: 'Not in your reply',
  nothingInReply: 'Nothing yet. Tap + on a point below to add it.',
  removeLabel: (text: string) => `Remove from your reply: ${text}`,
  addLabel: (text: string) => `Add to your reply: ${text}`,
  removedAnnouncement: (text: string) => `Removed from your reply: ${text}`,
  addedAnnouncement: (text: string) => `Added to your reply: ${text}`,
  mustFixRemaining: (count: number) =>
    count === 1 ? '1 must-fix suggestion not added' : `${count} must-fix suggestions not added`,
  swapTo: (next: string) => `Swap to ${next}`,
  source: (label: string) => `Source: ${label}`,
  openSource: (label: string) => `Open source: ${label}`,
  viewDetails: 'View details',
  sourceTitle: 'Source',
  backToDraft: 'Back to your draft',
  copyCitation: 'Copy citation',
  citationCopied: 'Copied the citation',
  sourceLoadFailed: "Couldn't load this source.",
  openLinkInstead: 'Open the page instead',
  viewDetailsLabel: (text: string) => `View details: ${text}`,
  mustFix: 'Must fix',
  noteLabel: 'Add your own point',
  notePlaceholder: 'Type a sentence to add',
  noteSubmit: 'Add point',
  addingNote: 'Adding your point…',

  useDraft: 'Write my reply',
  useDraftHint:
    'Writes your reply from these points, checks it, then puts it in the message box for you to edit and send',
  checking: 'Writing your reply…',
  useFailed: {
    timeout: 'The check did not finish. Try again.',
    empty_reply: 'Your reply is empty. Put a sentence back first.',
    other: 'Could not write your reply. Try again.',
  },
  mustFixHeading: 'Check before your reply goes in the message box',
  mustFixIntro:
    'Add each item to your reply, or confirm you have checked it. Nothing is sent until you send it yourself.',
  mustFixAdd: 'Add to reply',
  mustFixAddLabel: (label: string) => `Add to your reply: ${label}`,
  mustFixAcknowledge: 'I have checked this',
  mustFixAcknowledgeLabel: (label: string) => `I have checked: ${label}`,
  mustFixContinue: 'Continue',
  mustFixBack: 'Back to draft',
  replaceHeading: 'You edited this reply in the message box.',
  replaceBody: 'Replace your edited text with the updated draft?',
  replace: 'Replace',
  keepMine: 'Keep my text',

  tour: {
    replay: 'How this works',
    replayHint: 'Shows a short tour of drafting a reply',
    next: 'Next',
    gotIt: 'Got it',
    done: 'Done',
    steps: {
      decide: {
        1: 'Drafting a reply takes two steps: pick your guidance, then choose the points that go in your reply.',
        2: 'Research shows what changed recently, your similar cases and how peers answered. Open any row while you decide.',
        3: 'Pick the guidance closest to yours. Your draft starts from it, and you can come back and change it.',
      },
      review: {
        1: 'Tap − on a point you would not say, or + to put one in. Points move between the two lists below.',
        2: 'Missing something? Type your own point here and add it.',
        3: 'This writes your reply from these points and puts it in the message box. You edit and send it there. Nothing sends without you.',
      },
    } satisfies TourStepCopy,
  },

  conflict: 'This draft changed on another device. Showing the latest version.',
  saveFailed: 'Could not save that change. Try again.',
  unusable: {
    not_found: 'This draft is no longer available.',
    not_opted_in: 'Reply drafting is turned off for your account.',
    inquiry_closed: 'This inquiry is closed, so its draft can no longer be used.',
    invalid_status: 'This draft was already sent or replaced, so it can no longer be used.',
  } satisfies Partial<Record<ReplyDraftErrorKind, string>>,
};

/** The tour's copy for a zone, or `undefined` for a zone the tour does not define. */
export function tourStepText(segment: ReplyDraftTourSegment, zone: number): string | undefined {
  const steps: Partial<Record<number, string>> = REPLY_DRAFT_COPY.tour.steps[segment];

  return steps[zone];
}
